<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Production Station" avatarImage="/glass/img/production-overview-station.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>{{station.name}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="station.isActive" class="status-active">Active</span>
              <span v-if="!station.isActive" class="status-archive">Archived</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Availability">
              <span v-if="!station.isMaintenanceMode" class="status-active">Available</span>
              <LabelBadge labelClass="label-production-maintenance" v-if="station.isMaintenanceMode"><i class="fas fa-wrench mr-1"></i> In Maintenance</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Type">{{station.type | formatLookupName(ProductionStationTypeLookup)}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Location">{{station.accountLocation ? station.accountLocation.name : '-'}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Scanning">
              <LabelBadge labelClass="label-production-scanning-in mr-1" v-if="station.isScanInAvailable"><i class="fas fa-arrow-right mr-1"></i> Scan In</LabelBadge>
              <LabelBadge labelClass="label-production-scanning-out" v-if="station.isScanOutAvailable"><i class="fas fa-check-circle mr-1"></i> Scan Out</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Secondary" v-if="station.secondaryStation">
              <LinkProductionStation :id="station.secondaryStation.id" :name="station.secondaryStation.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Stages" v-if="station.productionStages">
              <template v-for="(stage, stageIndex) of station.productionStages">
                <br v-if="stageIndex > 0" :key="`sep-${stage.id}`" />
                <LinkProductionStage :key="stage.id" :id="stage.id" :name="stage.name" class="float-right" />
              </template>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{station.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{station.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-station-summary">
              <i class="far fa-clipboard-list-check"></i> Station Details
            </Tab>
            <Tab tabid="tab-station-hardware">
              <i class="far fa-microchip"></i> Hardware
            </Tab>
            <Tab tabid="tab-station-finance" v-if="userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionFinance)">
              <i class="far fa-usd-circle"></i> Finances
            </Tab>
            <Tab tabid="tab-station-toughening" v-if="isFurnaceStation">
              <i class="far fa-hammer"></i> Toughening Batches
            </Tab>
            <Tab tabid="tab-station-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-station-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            
            <TabPage :first="true" tabid="tab-station-summary">
              
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" @click="editStation" icon="fa-pencil">Edit Production Station</DropdownMenuItem>
                    <DropdownMenuItem v-if="canEditMaintenanceMode && !station.isMaintenanceMode" @click="editStationMaintenance" icon="fa-tools">Start Maintenance Mode</DropdownMenuItem>
                    <DropdownMenuItem v-if="canEditMaintenanceMode && station.isMaintenanceMode" @click="editStationMaintenance" icon="fa-tools">Update Maintenance Mode</DropdownMenuItem>
                    <DropdownMenuDivider v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" />
                    <template v-if="userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionStationApp) && station.productionStages">
                      <DropdownMenuItem v-for="stage of station.productionStages" :key="stage.id" :newTab="true" :link="`/apps/glass-production-station/${station.id}/stage/${stage.id}`" icon="fa-desktop">{{stage.name}} - Station View</DropdownMenuItem>
                    </template>
                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="printStockCuttingSheetReport" v-if="isCuttingStation" icon="fa-print">Print Stock Cutting Scan Sheet</DropdownMenuItem>
                    <DropdownMenuItem @click="archiveStation" v-if="station.isActive && userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" icon="fa-archive">Archive Production Station</DropdownMenuItem>
                    <DropdownMenuItem @click="restoreStation" v-if="!station.isActive && userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" icon="fa-undo">Restore Production Station</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{station.name}}</DataItem>
                  <DataItem title="Type">{{station.type | formatLookupName(ProductionStationTypeLookup)}}</DataItem>
                  <DataItem title="Location">{{station.accountLocation ? station.accountLocation.name : ''}}</DataItem>
                  <DataItem title="Scanning Type">{{scanTypeDescription}}</DataItem>
                  <DataItem title="Show Graph">{{station.showStationGraph | formatBoolean}}</DataItem>
                  <DataItem title="Unit Type">{{station.uomType | formatLookupName(UOMTypeLookup)}}</DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-2">
                <div class="col-12">
                  <h3>Capabilities</h3>
                  <StaticDataTable>
                    <template v-slot:header>
                      <tr>
                        <th>Setting</th>
                        <th>Value</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr><td>Glass Supported</td><td>{{station.glassType | formatLookupName(GlassTypeLookup)}}</td></tr>
                      <tr><td>Capacity</td><td>{{station.capacityUnitsPerHour}} <span v-html="formatLookupName(station.uomType, UOMTypeUnitsLookup)"></span> / hour</td></tr>
                      <tr><td>Thickness Range</td><td>{{station.minThickness}} - {{station.maxThickness}} mm</td></tr>
                      <tr><td>Minimum Panel Size (HxW)</td><td>{{station.sizeMinHeight}} x {{station.sizeMinWidth}} mm</td></tr>
                      <tr><td>Maximum Panel Size (HxW)</td><td>{{station.sizeMaxHeight}} x {{station.sizeMaxWidth}} mm</td></tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-station-hardware">
              <div class="row">
                <div class="col-12">
                  <ActionDropMenu v-if="isHardwareSettingsAvailable && !hardwareConfigurations.editing && userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" class="float-right">
                    <DropdownMenuItem @click="editStationHardware" icon="fa-pencil">Edit Hardware Settings</DropdownMenuItem>
                    <DropdownMenuItem @click="editHardwareConfigs" icon="fa-pencil">Edit Configuration List</DropdownMenuItem>
                  </ActionDropMenu>

                  <ContentTitle>Station Hardware Settings</ContentTitle>
                  <ContentSubTitle>Settings specific to station type the hardware and devices linked to the station</ContentSubTitle>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-12">
                  <StaticDataTable>
                    <template v-slot:header>
                      <tr>
                        <th>Setting</th>
                        <th>Value</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr>
                          <td>Hardware Type</td>
                          <td>{{formatLookupName(station.hardwareType, ProductionStationHardwareTypeLookup)}}</td>
                        </tr>
                      <template v-if="isWaterjetStation">
                        <tr>
                          <td>Waterjet Bed Size</td>
                          <td>{{waterjetBedSizeValue}}</td>
                        </tr>
                        <tr>
                          <td>Waterjet Positions</td>
                          <td>{{waterjetPositionValue}}</td>
                        </tr>
                        <tr>
                          <td>Waterjet Home Position</td>
                          <td>{{waterjetHomePositionValue}}</td>
                        </tr>
                        <tr>
                          <td>Waterjet Origin Point</td>
                          <td>{{waterjetOriginPointValue}}</td>
                        </tr>
                        <tr>
                          <td>Waterjet Origin Guide Size</td>
                          <td>{{waterjetOriginGuideSizeValue}}</td>
                        </tr>
                        <tr>
                          <td>Waterjet Max Negative Size</td>
                          <td>{{waterjetMaxNegativeSizeValue}}</td>
                        </tr>
                      </template>
                      <template v-if="isCuttingOptimisationStation">
                        <tr>
                          <td>Cutting Export - Origin Point</td>
                          <td>{{cuttingExportOriginPointValue}}</td>
                        </tr>
                        <tr>
                          <td>Optimiser - Fill Horizontal</td>
                          <td>{{optimiserFillHorzDirection}}</td>
                        </tr>
                        <tr>
                          <td>Optimiser - Fill Vertical</td>
                          <td>{{optimiserFillVertDirection}}</td>
                        </tr>
                        <tr>
                          <td>Optimiser - Max Cutting Depth</td>
                          <td>{{optimiserMaxCuttingDepth}}</td>
                        </tr>
                        <tr>
                          <td>Optimiser - Offcut Preference</td>
                          <td>{{optimiserOffcutScoringMethod}}</td>
                        </tr>
                        <tr v-if="isOptimiserOffcutLesserOffcut">
                          <td>Optimiser - Offcut Threshold</td>
                          <td>{{optimiserOffcutScoringThreshold}}%</td>
                        </tr>
                        <tr>
                          <td>Optimiser - Offcut Overflow Enabled</td>
                          <td>{{offcutOverflowEnabled}}</td>
                        </tr>
                      </template>

                      <template v-if="isFeedInStation">
                        <tr>
                          <td>Max Feed Height</td>
                          <td>{{station.maxFeedHeight}}mm</td>
                        </tr>
                      </template>

                      <template v-if="isFurnaceStation">
                        <tr>
                          <td>Loading Bed Size</td>
                          <td>{{furnaceBedSizeValue}}</td>
                        </tr>
                      </template>

                    </template>
                    <template v-slot:empty v-if="!isHardwareSettingsAvailable">
                      No settings available
                    </template>
                  </StaticDataTable>
                </div>
              </div>
              
              <FormHorizontalRule class="my-4" />

              <div class="mt-2">
                <ContentTitle>Station Hardware Configurations</ContentTitle>
                <ContentSubTitle>Station specific hardware rules / configurations based on glass panel thicknesses</ContentSubTitle>
                <FormGroup :form="hardwareConfigurations.form">
                  <div class="row mt-2">
                    <div class="col-12">
                      <StationHardwareConfigEditor
                        :editing="hardwareConfigurations.editing"
                        v-model="hardwareConfigurations.list"
                        :stationType="station.type" 
                      />
                    </div>
                  </div>
                  <template v-if="hardwareConfigurations.editing">
                    <FormHorizontalRule />
                    <FormSubmit label="Update" :submitting="hardwareConfigurations.submitting" @submitted="submitHardwareConfigs" @cancelled="cancelEditingHardwareConfigs" />
                  </template>
                </FormGroup>
              </div>
            </TabPage>

            <TabPage tabid="tab-station-finance" v-if="userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionFinance)">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionFinance) && !finances.editing" class="float-right">
                    <DropdownMenuItem @click="finances.editing = true" icon="fa-pencil">Edit Finances</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Station Operating Costs</h3>
                </div>
              </div>
              <FormGroup :labelColumnSize="6" :form="finances.form" v-if="station.productionOperatingCosts">
                <div class="row mt-2">
                  <div class="col-12">
                    <ProductionOperatingCostList :uomType="station.uomType" v-model="station.productionOperatingCosts" :editing="finances.editing" />
                  </div>
                </div>
                <template v-if="finances.editing">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="finances.submitting" @submitted="submitStationFinances" @cancelled="cancelEditingFinances" />
                </template>
              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-station-toughening" v-if="isFurnaceStation">
              <TougheningBatchDataTable :productionStationId="station.id" />
            </TabPage>

            <TabPage tabid="tab-station-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.productionStation"
                    :linkId="station.id"
                    :permissionResource="PermissionTypes.ProductionPermissionTypes.productionConfig"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-station-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.productionStation" :linkId="station.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'

import DocumentList from '@/components/documentList.vue'
import TougheningBatchDataTable from '@/modules/production/components/tougheningBatchDataTable'
import StationHardwareConfigEditor from '@/modules/production/components/stationHardwareConfigEditor'
import ProductionOperatingCostList from '@/modules/production/components/productionOperatingCostList'
import ProductionStationDialog from '@/modules/production/views/productionStationDialog'
import ProductionStationHardwareDialog from '@/modules/production/views/productionStationHardwareDialog'
import ProductionStationMaintenanceDialog from '@/modules/production/views/productionStationMaintenanceDialog'

import { LinkTypes, ProductionStationTypes, ProductionStationTypeLookup, ProductionOptimisationCutTypeLookup, GlassTypeLookup, UOMTypeLookup, UOMTypeUnitsLookup, ProductionStationHardwareTypes, ProductionStationHardwareTypeLookup } from '@/store/types'
import { OptimiserFillDirectionTypes, OptimiserFillDirectionTypeLookup, OffcutScoreConfigTypes, OffcutScoreConfigTypeLookup } from '@/store/types'
import { AlignmentTypes, AlignmentTypeLookup } from '@/store/mathTypes'

export default {
  name: 'ProductionStation',
  mixins: [form],
  components: {
    DocumentList,
    TougheningBatchDataTable,
    StationHardwareConfigEditor,
    ProductionOperatingCostList
  },
  data() {
    return {
      station: {},
      finances: {
        editing: false,
        submitting: false,
        form: {}
      },
      hardwareConfigurations: {
        editing: false,
        submitting: false,
        list: [],
      },
    }
  },
  computed: {
    activityStreamURL() {
      return `/production/stations/${this.$route.params.id}/activitystream`
    },
    scanTypeDescription() {
      if(this.station.isScanInAvailable && this.station.isScanOutAvailable)
        return 'In and Out'
      if(this.station.isScanInAvailable)
        return 'In Only'
      if(this.station.isScanOutAvailable)
        return 'Out Only'
      return 'None'
    },
    canEditMaintenanceMode() {
      return true
    },
    isCuttingStation() {
      return this.$data.station.type == ProductionStationTypes.cutting
    },
    isCuttingOptimisationStation() {
      return this.$data.station.type == ProductionStationTypes.cuttingOptimisation
    },
    isWaterjetStation() {
      return this.$data.station.type == ProductionStationTypes.waterjet
    },
    isFurnaceStation() {
      return this.$data.station.type == ProductionStationTypes.furnace
    },
    isFeedInStation() {
      return this.$data.station.type == ProductionStationTypes.feedIn
    },
    isHardwareSettingsAvailable() {
      return true
    },
    waterjetBedSizeValue() {
      let bedConfig = this.$data.station.waterjetBedConfig || {}
      let width = bedConfig.width || 0
      let height = bedConfig.height || 0

      return `Height: ${height}mm, Width: ${width}mm`
    },
    waterjetPositionValue() {
      let bedConfig = this.$data.station.waterjetBedConfig || {}
      let list = bedConfig.positionList || [0]
      return list.map(position => {
        return parseInt(position) + 'mm'
      }).join(', ')
    },
    waterjetOriginPointValue() {
      let bedConfig = this.$data.station.waterjetBedConfig || {}
      let originHorizontal = bedConfig.originHorizontal || AlignmentTypes.left
      let originVertical = bedConfig.originVertical || AlignmentTypes.bottom

      return Utils.getLookupName(AlignmentTypeLookup, originVertical) + ' ' + Utils.getLookupName(AlignmentTypeLookup, originHorizontal)
    },
    waterjetHomePositionValue() {
      let bedConfig = this.$data.station.waterjetBedConfig || {}
      let homePositionX = bedConfig.homePositionX || 0
      let homePositionY = bedConfig.homePositionY || 0

      return `X: ${homePositionX}mm, Y: ${homePositionY}mm`
    },
    waterjetOriginGuideSizeValue() {
      let bedConfig = this.$data.station.waterjetBedConfig || {}
      let originGuideHeight = bedConfig.originGuideHeight || 0
      let originGuideWidth = bedConfig.originGuideWidth || 0

      return `Height: ${originGuideHeight}mm, Width: ${originGuideWidth}mm`
    },
    waterjetMaxNegativeSizeValue() {
      let bedConfig = this.$data.station.waterjetBedConfig || {}
      let maxNegativeHeight = bedConfig.maxNegativeHeight || 0
      let maxNegativeWidth = bedConfig.maxNegativeWidth || 0

      return `Height: ${maxNegativeHeight}mm, Width: ${maxNegativeWidth}mm`
    },
    cuttingExportOriginPointValue() {
      let optiConfig = this.$data.station.optimiserConfig || {}
      let exportOriginHorizontal = optiConfig.exportOriginHorizontal || AlignmentTypes.left
      let exportOriginVertical = optiConfig.exportOriginVertical || AlignmentTypes.bottom

      return Utils.getLookupName(AlignmentTypeLookup, exportOriginVertical) + ' ' + Utils.getLookupName(AlignmentTypeLookup, exportOriginHorizontal)
    },
    optimiserFillHorzDirection() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}
      let fillHorzDir = optimiserConfig.fillHorzDirection || OptimiserFillDirectionTypes.right

      return Utils.getLookupName(OptimiserFillDirectionTypeLookup, fillHorzDir)
    },
    optimiserFillVertDirection() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}
      let fillVertDir = optimiserConfig.fillVertDirection || OptimiserFillDirectionTypes.up

      return Utils.getLookupName(OptimiserFillDirectionTypeLookup, fillVertDir)
    },
    optimiserOffcutScoringMethod() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}
      let offcutScoreMethod = optimiserConfig.offcutScoringMethod || OffcutScoreConfigTypes.greaterOffcutArea

      return Utils.getLookupName(OffcutScoreConfigTypeLookup, offcutScoreMethod)
    },
    isOptimiserOffcutLesserOffcut() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}
      let offcutScoreMethod = optimiserConfig.offcutScoringMethod || OffcutScoreConfigTypes.greaterOffcutArea

      return offcutScoreMethod == OffcutScoreConfigTypes.lesserOffcutCount
    },
    optimiserOffcutScoringThreshold() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}
      let offcutScoreMethod = parseFloat(optimiserConfig.offcutScoringPercent)
      if(isNaN(offcutScoreMethod))
        offcutScoreMethod = 50

      return offcutScoreMethod
    },
    offcutOverflowEnabled() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}

      return this.formatBoolean(!!optimiserConfig.offcutOverflowEnabled)
    },
    optimiserMaxCuttingDepth() {
      let optimiserConfig = this.$data.station.optimiserConfig || {}
      let maxCuttingDepth = parseInt(optimiserConfig.maxCuttingDepth)
      if(isNaN(maxCuttingDepth))
        maxCuttingDepth = 9

      return maxCuttingDepth
    },
    furnaceBedSizeValue() {
      let furnaceConfig = this.$data.station.furnaceConfig || {}
      let width = furnaceConfig.bedWidth || 0
      let height = furnaceConfig.bedHeight || 0

      return `Height: ${height}mm, Width: ${width}mm`
    },
  },
  created() {
    this.LinkTypes = LinkTypes
    this.ProductionStationTypeLookup = ProductionStationTypeLookup
    this.ProductionOptimisationCutTypeLookup = ProductionOptimisationCutTypeLookup
    this.GlassTypeLookup = GlassTypeLookup
    this.UOMTypeLookup = UOMTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
    this.ProductionStationHardwareTypeLookup = ProductionStationHardwareTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.productionService.getProductionStation(this.$route.params.id),
      ]))
      .then(results => {
        this.station = results[0].data
        this.hardwareConfigurations.list = _.cloneDeep(this.station.productionStationHardwareConfigs)
      })
      .catch(err => this.handlePageLoadError(err))
    },

    editStation() {
      this.displayModal(ProductionStationDialog, { 
        productionStationId: this.$data.station.id,
      },
      this.$data.station)
      .then((success) => {
        if(success) {
          this.successToast('Production Station Updated')
          this.reloadData()
        }
      })
    },
    editStationMaintenance() {
      this.displayModal(ProductionStationMaintenanceDialog, { 
        productionStationId: this.$data.station.id,
      },
      this.$data.station)
      .then((success) => {
        if(success) {
          this.successToast('Maintenance Mode Updated')
          this.reloadData()
        }
      })
    },
    archiveStation() {
      this.confirmDeleteMessage('Archive Production Station?', 'Are you sure you want to archive this production station?')
      .then(success => {
        if(success) {
          this.$apiService.productionService.archiveProductionStation(this.$route.params.id).then(response => {
            this.successToast('Production Station Archived')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not archive production station') })
        }
      })
    },
    restoreStation() {
      this.confirmDeleteMessage('Restore Production Station?', 'Are you sure you want to restore this production station?')
      .then(success => {
        if(success) {
          this.$apiService.productionService.restoreProductionStation(this.$route.params.id).then(response => {
            this.successToast('Production Station Restored')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not restore production station') })
        }
      })
    },
    editStationHardware() {
      this.displayModal(ProductionStationHardwareDialog, { 
        productionStationId: this.$data.station.id,
      },
      this.$data.station)
      .then((success) => {
        if(success) {
          this.successToast('Production Station Hardware Updated')
          this.reloadData()
        }
      })
    },

    // Station Finances
    submitStationFinances() {
      let costListData = this.station.productionOperatingCosts.map(cost => {
        var outputData = _.cloneDeep(cost)
        if(cost.isNewCost)
          delete outputData.id
        return outputData
      })

      this.$data.finances.form = this.clearFormResponse()
      this.$data.finances.submitting = true
      this.$apiService.productionService.updateProductionStationFinances(this.$route.params.id, { productionOperatingCosts: costListData })
      .then(response => {
        this.$data.finances.editing = false
        this.successToast('Station Finances updated')
      })
      .catch(err => {
        this.$data.finances.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.finances.submitting = false
      })
    },
    cancelEditingFinances() {
      this.$data.finances.editing = false
      this.requestData()
    },

    // Hardware Configurations
    editHardwareConfigs() {
      this.hardwareConfigurations.editing = true
      this.$data.hardwareConfigurations.form = this.clearFormResponse()
    },
    cancelEditingHardwareConfigs() {
      this.hardwareConfigurations.editing = false
      this.reloadData()
    },
    submitHardwareConfigs() {
      this.hardwareConfigurations.submitting = true
      this.$data.hardwareConfigurations.form = this.clearFormResponse()

      var updateData = {
        productionStationHardwareConfigs: this.hardwareConfigurations.list.map(config => {
          var outputData = _.cloneDeep(config)
          if(config.isNew)
            delete outputData.id
          return outputData
        })
      }

      this.$apiService.productionService.updateProductionStationHardware(this.$route.params.id, updateData)
      .then((response) => {
        this.hardwareConfigurations.editing = false
        this.successToast('Order Editor Guidelines Updated')
        this.reloadData()
      })
      .catch(err => {
        this.$data.hardwareConfigurations.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.hardwareConfigurations.submitting = false
      })
    },
    printStockCuttingSheetReport() {
      this.$apiService.reportingService.generateReportSignedURL(`/reporting/production/stations/${this.$route.params.id}/cuttingscansheet`)
      .then(response => {
        window.open(response.data.url, '_blank').focus();
      })
    }
  }
}
</script>